const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000/api';

const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

export async function getPlanesEstudio(id_programa) {
  const res = await fetch(`${API_BASE}/escolar/planes-estudio/?id_programa=${id_programa}`, {
    headers: getAuthHeaders()
  });
  if (!res.ok) throw new Error(`GET /planes-estudio/ ${res.status}`);
  return res.json();
}

/**
 * Obtiene las materias asociadas a un programa desde planes de estudio
 * @param {number} idPrograma - ID del programa académico
 * @returns {Promise<Array>} - Lista de materias con formato para UI
 */
export async function getMateriasPorPrograma(idPrograma) {
  if (!idPrograma) {
    console.warn('[planestudio.js] getMateriasPorPrograma: idPrograma es null/undefined');
    return [];
  }
  
  console.log('[planestudio.js] Buscando materias para programa:', idPrograma);
  const url = `${API_BASE}/escolar/planes-estudio/?id_programa=${idPrograma}`;
  console.log('[planestudio.js] URL:', url);
  
  try {
    const res = await fetch(url, {
      headers: getAuthHeaders()
    });
    
    if (!res.ok) {
      console.error('[planestudio.js] Error obteniendo materias por programa:', res.status);
      return [];
    }
    
    const data = await res.json();
    console.log('[planestudio.js] Respuesta API planes-estudio:', data);
    
    // Puede ser un array directo o un objeto paginado con results
    const planes = Array.isArray(data) ? data : (data.results || []);
    console.log('[planestudio.js] Planes encontrados:', planes.length);
    
    // Mapear planes a formato de materias para la UI
    const materias = planes.map(plan => {
      console.log('[planestudio.js] Plan individual:', {
        id_materia: plan.id_materia,
        periodo_sugerido: plan.periodo_sugerido,
        materia_nombre: plan.materia?.nombre
      });
      return {
        id: plan.materia?.id || plan.id_materia,
        nombre: plan.materia?.nombre || '',
        nombre_materia: plan.materia?.nombre || '',
        clave: plan.materia?.clave || '',
        id_seccion: plan.materia?.id_seccion || plan.materia?.id_seccion_id || null,
        creditos: plan.materia?.creditos ?? plan.creditos_asignados ?? 0,
        tipo: plan.materia?.tipo || '',
        periodo_sugerido: plan.periodo_sugerido,
        es_obligatoria: plan.es_obligatoria,
        _fromPlan: true // Indicador de que viene del plan de estudios
      };
    });
    
    console.log('[planestudio.js] Materias mapeadas:', materias);
    return materias;
  } catch (error) {
    console.error('[planestudio.js] Error obteniendo materias por programa:', error);
    return [];
  }
}

/**
 * Parsea un valor a número entero, retornando null si no es válido
 * @param {*} value - Valor a parsear
 * @returns {number|null} - Número parseado o null
 */
function parseCreditos(value) {
  // Si es null, undefined, o string vacío, retornar null
  if (value === null || value === undefined || value === '' || value === '—') {
    return null;
  }
  // Intentar convertir a número
  const parsed = parseInt(value, 10);
  // Si no es un número válido, retornar null
  return isNaN(parsed) ? null : parsed;
}

/**
 * Guarda el plan de estudios completo para un programa
 * @param {number} programaId - ID del programa académico
 * @param {Array} periods - Array de períodos con sus materias asignadas
 * @returns {Promise<Object>} - Resultado del guardado
 */
export async function savePlanEstudio(programaId, periods) {
  if (!programaId) throw new Error('ID de programa no proporcionado');
  
  // Transformar la estructura de períodos a la estructura del backend
  const materias = [];
  let ordenSecuencia = 1;
  
  for (const period of periods) {
    for (const subject of period.subjects) {
      materias.push({
        id_materia: subject.id,
        periodo_sugerido: period.number,
        creditos_asignados: parseCreditos(subject.credits),
        es_obligatoria: true,
        orden_secuencia: ordenSecuencia++,
        activo: true
      });
    }
  }
  
  const payload = {
    id_programa: programaId,
    materias: materias
  };
  
  const res = await fetch(`${API_BASE}/escolar/planes-estudio/guardar-completo/`, {
    method: 'POST',
    headers: { 
      'Content-Type': 'application/json', 
      ...getAuthHeaders() 
    },
    body: JSON.stringify(payload),
  });
  
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Error guardando plan de estudios: ${res.status} - ${text}`);
  }
  
  return res.json();
}

/**
 * Obtiene todas las materias de una sección académica
 * @param {number} seccionId - ID de la sección académica
 * @param {number|null} excluirPrograma - ID del programa cuyas materias ya del plan se excluyen (opcional)
 * @returns {Promise<Array>} - Lista de materias
 */
export async function getMateriasPorSeccion(seccionId, excluirPrograma = null) {
  if (!seccionId) {
    console.warn('[planestudio.js] getMateriasPorSeccion: seccionId es null/undefined');
    return [];
  }
  
  let url = `${API_BASE}/escolar/materias/por-seccion/${seccionId}/`;
  if (excluirPrograma) {
    url += `?excluir_programa=${excluirPrograma}`;
  }
  
  try {
    const res = await fetch(url, {
      headers: getAuthHeaders()
    });
    
    if (!res.ok) {
      console.error('[planestudio.js] Error obteniendo materias por sección:', res.status);
      return [];
    }
    
    const data = await res.json();
    const materias = Array.isArray(data) ? data : (data.results || []);
    
    return materias.map(m => ({
      id: m.id_materia || m.id,
      nombre: m.nombre_materia || m.nombre || '',
      nombre_materia: m.nombre_materia || m.nombre || '',
      clave: m.clave_materia || m.clave || '',
      creditos: m.creditos || 0,
      tipo: m.tipo_materia || '',
      seccion: m.seccion_nombre || '',
      _fromSeccion: true
    }));
  } catch (error) {
    console.error('[planestudio.js] Error obteniendo materias por sección:', error);
    return [];
  }
}